import { useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Base z-index for the backdrop; the sheet panel sits at zIndex + 1. Defaults to 150. */
  zIndex?: number;
  children: React.ReactNode;
}

const DISMISS_THRESHOLD = 80;

/**
 * Reusable bottom sheet with drag-to-dismiss, backdrop, and consistent chrome.
 * Wrap any sheet content with this instead of reimplementing touch handling.
 */
export default function Sheet({ isOpen, onClose, zIndex = 150, children }: SheetProps) {
  const [dragY, setDragY] = useState(0);
  const startYRef = useRef<number | null>(null);

  if (!isOpen) return null;

  const handleTouchStart = (e: React.TouchEvent) => {
    startYRef.current = e.touches[0]!.clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (startYRef.current === null) return;
    const delta = e.touches[0]!.clientY - startYRef.current;
    if (delta > 0) setDragY(delta);
  };
  const handleTouchEnd = () => {
    if (dragY > DISMISS_THRESHOLD) onClose();
    setDragY(0);
    startYRef.current = null;
  };

  const opacity = Math.max(0, 1 - dragY / 300);

  return (
    <>
      <Box
        position="fixed"
        inset={0}
        bg="blackAlpha.400"
        zIndex={zIndex}
        onClick={onClose}
        style={{ opacity }}
      />
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        bg="bg.surface"
        borderTopRadius="20px"
        zIndex={zIndex + 1}
        boxShadow="0 -8px 32px rgba(0,0,0,0.10)"
        pb="calc(env(safe-area-inset-bottom) + 8px)"
        style={{ transform: `translateY(${dragY}px)`, transition: dragY === 0 ? 'transform 0.3s ease' : 'none' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle */}
        <Box display="flex" justifyContent="center" pt="10px" pb="4px" cursor="grab">
          <Box w="36px" h="4px" borderRadius="2px" bg="border.muted" />
        </Box>

        {children}
      </Box>
    </>
  );
}
